import { useEffect, useMemo, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import MapWithNavigation from '../LazyMapWithNavigation';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  TripLeg,
  TripLegAddress,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useDriverLocation } from '@/hooks/useDriverLocation';

const PREFIX = 'TripLegLiveTracking';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  map: `${PREFIX}-map`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 400,
    width: '100%',
  },
  [`& .${classes.header}`]: {
    marginTop: theme.spacing(2.25),
  },
  [`& .${classes.map}`]: {
    height: 400,
    width: '100%',
    '& div.leaflet-routing-alternatives-container': {
      display: 'none',
    },
  },
}));

const TripLegLiveTracking = ({ tripLeg }: { tripLeg: TripLeg }) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [tripEndPoints, setTripEndPoints] = useState<{
    pickupAddress: TripLegAddress;
    dropoffAddress: TripLegAddress;
  }>();
  const [center, setCenter] = useState({
    lat: 0,
    lng: 0,
  });

  const driverLocation = useDriverLocation({
    driverID: tripLeg?.driverID,
    tripLegID: tripLeg?.tripLegID,
  });

  useEffect(() => {
    if (tripLeg) {
      setCenter({
        lat: tripLeg.pickUpAddress.latitude || 0,
        lng: tripLeg.pickUpAddress.longitude || 0,
      });
      setTripEndPoints({
        pickupAddress: tripLeg.pickUpAddress,
        dropoffAddress: tripLeg.dropOffAddress,
      });
    }
  }, [dispatch, tripLeg]);

  const isLive = useMemo(() => {
    return (
      tripLeg.status === TripLegStatus.Confirmed ||
      tripLeg.status === TripLegStatus.InTransit ||
      tripLeg.status === TripLegStatus.Onsite ||
      tripLeg.status === TripLegStatus.Onboard
    );
  }, [tripLeg]);
  return (
    <StyledBox className={classes.container}>
      <Card>
        <CardContent>
          {isLive && (
            <Typography className={classes.header} variant="h3">
              {t('reservation.liveTracking')}
            </Typography>
          )}
          {tripLeg?.pickUpAddress?.latitude && (
            <MapContainer
              className={classes.map}
              zoom={10}
              center={{
                lat: tripLeg?.pickUpAddress?.latitude || 0,
                lng: tripLeg?.pickUpAddress?.longitude || 0,
              }}>
              <MapWithNavigation
                tripLegs={tripEndPoints ? [tripEndPoints] : []}
                driverLocation={driverLocation}
                fitBounds={true}
              />
            </MapContainer>
          )}
        </CardContent>
      </Card>
    </StyledBox>
  );
};

export default TripLegLiveTracking;
